// What he said about you, in words the person reading can follow.
//
// The mentions tab used to print his diary line raw: the pill said
// "mention", the body said "Under the note sleepyCrow64 left me, to
// sleepyCrow64: Got it, I'll keep hunting for a model. Under the note
// august left me, to august: I haven't read Levin yet" and then ran out
// of characters. Measured against production on 25 Sep: every single
// answered_talk row in the file packs up to three replies into one
// entry, so august opened their own page and read sleepyCrow64's
// conversation with their own reply cut off in the middle.
//
// The diary entry is not wrong. It is his, and one sitting with the
// board IS one thing he did. Which part of it belongs to YOU is a viewer
// idea, same argument as /api/mentions doing the scan rather than the
// sim: the sim writes his life, and whose life it touches only means
// anything once somebody is signed in.
//
// So this is the door. A raw row in, one readable thing out: what
// happened, said from your side; his actual words, pulled out of the
// machinery around them; and your own words underneath when the entry
// remembers what you left. Pure, so the copy can be pinned.

// The joined line a sitting with the board writes (Board.js). Entries
// written from 25 Sep carry `said` as data; this takes apart the ones
// already on disk, and there is no expiry on those: "he mentioned you
// once, a week ago" is exactly the line somebody comes back to find.
const PART = /Under the (note|picture) (\S+) left me, to ([^:]{1,120}): /g
const LAST_WORD = /\s*That was my last word on it\.\s*$/

// His own factual openers, which the entry needs and the reader does
// not: the headline already says he went back to what you left.
const REVISIT_OPEN = /^Took down the (?:note|picture) \S+ left me, up in the ruins\.\s*Still good\.\s*/i
const RELEASED_OPEN = /^Took the (?:note|picture) \S+ left me down from the ruins, for good\.\s*/i

const same = (a, b) => String(a || '').toLowerCase() === String(b || '').toLowerCase()
const oneOf = (list, names) => (list || []).some((n) => (names || []).some((m) => same(n, m)))

/** "a, b and c" back into ['a','b','c']. The join is Talk.js sentence(). */
function unsentence(s) {
  return String(s || '')
    .split(/,\s*|\s+and\s+/)
    .map((n) => n.trim())
    .filter(Boolean)
}

/**
 * The parts of one sitting with the board, from the joined diary line.
 * Each: whose thing it was under, who he was talking to, what he said.
 */
export function talkParts(detail) {
  const text = String(detail || '')
  const heads = []
  PART.lastIndex = 0
  let m = PART.exec(text)
  while (m) {
    heads.push({ at: m.index, end: PART.lastIndex, gifted: m[1], owner: m[2], to: unsentence(m[3]) })
    m = PART.exec(text)
  }
  return heads.map((h, i) => {
    const body = text.slice(h.end, i + 1 < heads.length ? heads[i + 1].at : text.length).trim()
    return {
      owner: h.owner,
      gifted: h.gifted,
      to: h.to,
      reply: body.replace(LAST_WORD, '').trim(),
      closing: LAST_WORD.test(body),
    }
  })
}

/** The part of a sitting that is yours, or null when none of it is. */
export function myPart(parts, names) {
  const list = parts || []
  return list.find((p) => oneOf(p.to, names))
    || list.find((p) => same(p.owner, names?.[0]) || (names || []).some((n) => same(p.owner, n)))
    || null
}

// ── The words ───────────────────────────────────────────────────────────

/**
 * One mention, read from your side.
 *
 * { headline }  what happened, in your terms, never his machinery
 * { said }      his actual words, when the entry holds any
 * { theirs }    what you left him, when the entry remembers it
 * { group }     which filter it belongs to
 * { closing }   he has said his last word on that thread
 */
export function readMention(m, { names = [] } = {}) {
  const kind = String(m?.kind || '')
  const detail = String(m?.line || '')
  // note or picture, worked out by the route where the journal's own
  // field names live. Nothing here knows what an entry looks like.
  const gifted = m?.gifted || 'thing'
  const out = {
    ts: m?.ts || 0,
    kind,
    ref: m?.ref ?? null,
    headline: '',
    said: '',
    aside: '',
    theirs: m?.about ? String(m.about) : '',
    group: 'other',
    closing: false,
  }

  if (kind === 'answered_talk') {
    // `said` from the sim, the joined line from the file, in that order.
    const parts = Array.isArray(m?.said) ? m.said : talkParts(detail)
    const mine = myPart(parts, names)
    if (mine) {
      const yours = (names || []).some((n) => same(mine.owner, n))
      out.said = mine.reply
      out.closing = !!mine.closing
      out.group = 'offerings'
      if (oneOf(mine.to, names)) {
        out.headline = yours
          ? `He answered you under the ${mine.gifted} you left him.`
          : `He answered you under ${mine.owner}'s ${mine.gifted}.`
      } else {
        const who = mine.to.length ? mine.to.join(' and ') : 'somebody'
        out.headline = `He answered ${who} under the ${mine.gifted} you left him.`
      }
      return out
    }
    // Named on the entry but in none of its parts. You were in one of the
    // threads he answered without being the one he answered: `others`
    // holds everybody who has written under a thing, not just the person
    // he wrote back to. Which thread is not knowable unless there was
    // only one, so his words are only shown when there is no doubt.
    out.headline = 'He answered in a thread you had written in.'
    out.group = 'offerings'
    if (parts.length === 1) out.said = parts[0].reply
    return out
  }

  if (kind === 'sacrifice_judged') {
    out.said = detail
    out.group = 'offerings'
    out.headline = m?.verdict === 'reject'
      ? `He let the ${gifted} you left him go.`
      : `He kept the ${gifted} you left him.`
    return out
  }

  if (kind === 'revisited_offering') {
    out.said = detail.replace(REVISIT_OPEN, '').trim()
    out.group = 'offerings'
    out.headline = `He went back to the ${gifted} you left him, and kept it.`
    return out
  }

  if (kind === 'released_offering') {
    out.said = detail.replace(RELEASED_OPEN, '').trim()
    out.group = 'offerings'
    out.headline = `He took the ${gifted} you left him down off the wall, for good.`
    return out
  }

  if (kind === 'told_them') {
    out.said = detail
    out.group = 'said'
    out.headline = m?.question
      ? `He went and looked into ${m.question}, and came back to you.`
      : 'He came back to you about something you asked.'
    return out
  }

  if (kind === 'wrote_email') {
    out.group = 'letters'
    out.headline = 'He wrote you a letter from the internet cafe.'
    out.said = ''
    // The entry is the fact, not a letter; the line is his note of it.
    out.aside = detail
    return out
  }

  const THINGS = {
    sold_print: 'You bought a print of one of his drawings.',
    bought_for_him: 'You bought him something out of his vending machine.',
    sold_stickers: 'You bought a pack of his stickers.',
    posted_print: 'He put your print in the post.',
    bought_thing: 'Your money went on something for the town.',
    found: 'He found the thing you bought him.',
    used: 'He used the thing you bought him.',
    wearing: 'The thing you bought him is wearing out.',
    ended: 'The thing you bought him wore out.',
    spoiled: 'The thing you bought him went off.',
  }
  if (THINGS[kind]) {
    out.headline = THINGS[kind]
    out.said = detail
    out.group = 'things'
    return out
  }

  if (kind === 'noticed_something') {
    out.headline = 'He came across something you did in his town.'
    out.said = detail
    out.group = 'deeds'
    return out
  }

  // Anything he says your name in that has no shape of its own. The line
  // is his, so it stays his words rather than being dressed as an event.
  out.headline = 'He said your name.'
  out.said = detail
  out.group = 'said'
  return out
}

// ── Grouping ────────────────────────────────────────────────────────────

const DAY = 86_400_000

/** today / yesterday / a weekday this week / "14 September". */
export function dayLabel(ts, now = Date.now()) {
  const d = new Date(ts)
  const midnight = new Date(now); midnight.setHours(0, 0, 0, 0)
  const start = midnight.getTime()
  if (ts >= start) return 'today'
  if (ts >= start - DAY) return 'yesterday'
  if (ts >= start - DAY * 6) return d.toLocaleDateString(undefined, { weekday: 'long' })
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'long' })
}

/** Rows into days, newest first, each day's rows newest first. */
export function byDay(rows, now = Date.now()) {
  const out = []
  for (const r of rows || []) {
    const label = dayLabel(r.ts, now)
    const last = out[out.length - 1]
    if (last && last.label === label) last.rows.push(r)
    else out.push({ label, rows: [r] })
  }
  return out
}

// The filters, in the order they read. Only the ones with rows in them
// are ever drawn: a chip that empties the list is a chip that lies.
export const GROUPS = [
  { id: 'said', label: 'his words' },
  { id: 'offerings', label: 'your offerings' },
  { id: 'things', label: 'your things' },
  { id: 'deeds', label: 'your deeds' },
  { id: 'letters', label: 'letters' },
]

/** How many rows sit in each group, for the chips. */
export function groupCounts(rows) {
  const counts = {}
  for (const r of rows || []) counts[r.group] = (counts[r.group] || 0) + 1
  return counts
}
